import { Eye, EyeOff } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { dialogCopy, type VisibilityTarget } from './visibilityCopy';

/** The icon-led title both directions show, and Make Visible & Notify's. */
export const VisibilityHeading = ({
  target,
  title
}: {
  target: VisibilityTarget;
  title: string;
}) => {
  const makingVisible = target === 'visible';
  const Icon = makingVisible ? Eye : EyeOff;
  return (
    <span className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
          makingVisible
            ? 'bg-success-soft text-success'
            : 'bg-service-property-soft text-service-property-soft-foreground'
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      {title}
    </span>
  );
};

interface PhotosetVisibilityDialogProps {
  target: VisibilityTarget;
  accountName: string | null;
  saving: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * DV-2861: the confirmation both directions share. Presentational: the
 * target was captured when the dialog opened (usePhotosetVisibility), so it
 * renders from that, never from the live flag.
 *
 * Hide is deliberately NOT the destructive variant (spec decisions log): the
 * Trash dialog stays the only destructive one. Its fill is the property-red
 * token, with `text-background` rather than the prototype's literal white,
 * which fails contrast on the dark ramp's salmon.
 */
const PhotosetVisibilityDialog = ({
  target,
  accountName,
  saving,
  error,
  onConfirm,
  onCancel
}: PhotosetVisibilityDialogProps) => {
  const copy = dialogCopy(target, accountName);
  const makingVisible = target === 'visible';
  const Icon = makingVisible ? Eye : EyeOff;

  return (
    <Modal
      open
      // Escape, the backdrop and the corner X all arrive here; the hook
      // refuses them while a save is in flight.
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      size="md"
      title={<VisibilityHeading target={target} title={copy.title} />}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            autoFocus
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className={cn(
              !makingVisible &&
                'bg-service-property-soft-foreground text-background hover:bg-service-property-soft-foreground/90'
            )}
          >
            {saving ? (
              <Spinner size="sm" className="text-current" />
            ) : (
              <Icon className="h-4 w-4" />
            )}
            {copy.confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Typography as="p" variant="body" className="text-muted-foreground">
          {copy.body}
        </Typography>
        {copy.reassurance && (
          <Typography as="p" variant="body" className="text-muted-foreground">
            {copy.reassurance}
          </Typography>
        )}
        {error && (
          <Typography
            as="p"
            variant="body"
            role="alert"
            className="text-destructive"
          >
            {error}
          </Typography>
        )}
      </div>
    </Modal>
  );
};

export default PhotosetVisibilityDialog;
